import React from 'react';
import { useOneClickStore } from '@/stores/oneClickStore';
import { useAttackStore } from '@/stores/attackStore';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';

interface Props {
  children: React.ReactNode;
  /** Changes recover a FAILED route only; healthy children are never re-keyed. */
  resetKey?: string;
}

interface State {
  failed: boolean;
  detail: string;
}

/** Render/lifecycle containment, not a catch for event handlers, async callbacks
 * or browser OOM. Never retries a trade or remounts a failed view automatically.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { failed: false, detail: '' };

  static getDerivedStateFromError(error: unknown): State {
    return {
      failed: true,
      detail: error instanceof Error ? `${error.name}: ${error.message}`.slice(0, 600) : 'Unknown rendering error',
    };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // Local safety only: preserve in-flight requests, receipts, account state
    // and preferences. No cancel/retry API. One subscriber failure must not
    // prevent the other controls being made safe.
    for (const stop of [
      // The ordinary disarm control clears lastAck; a crash must retain it.
      () => useOneClickStore.setState({ armed: false }),
      () => useAttackStore.getState().disarmAttack(),
      () => useLt2FocusStore.getState().clearFocus(),
    ]) {
      try { stop(); } catch (safetyError) { console.error('[G1 view safety]', safetyError); }
    }
    console.error('[G1 view error]', error, info.componentStack);
    // Do not claim server receipt: /relay/client-log is not installed on every
    // deployment. Details stay local and user-visible.
  }

  componentDidUpdate(previous: Props): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false, detail: '' });
    }
  }

  render(): React.ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <section role="alert" className="bg-background text-foreground min-w-0 p-6">
        <div className="mx-auto max-w-xl space-y-3 rounded-lg border border-border bg-card p-5">
          <h1 className="text-base font-semibold">This view couldn’t load</h1>
          <p className="text-sm text-muted-foreground">
            Trading shortcuts have been disarmed. Check your orders before trying again:
            this error does not tell us whether a pending order completed.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <a href="/dashboard" className="rounded border border-border px-3 py-2 hover:bg-muted">Back to dashboard</a>
            <button type="button" onClick={() => window.location.reload()} className="rounded border border-border px-3 py-2 hover:bg-muted">Reload page</button>
          </div>
          <details className="min-w-0 text-xs text-muted-foreground">
            <summary className="cursor-pointer">Technical details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{this.state.detail}</pre>
          </details>
        </div>
      </section>
    );
  }
}

export default ErrorBoundary;
